/**
 * A small Markdown reader for agent answers: paragraphs, headings, lists,
 * tables, code, bold, italic, inline code and https links. It returns plain
 * data that the renderer turns into React elements, so text from the agent is
 * never interpreted as HTML.
 */

export type MarkdownInline =
  | { type: "text"; text: string }
  | { type: "strong"; children: MarkdownInline[] }
  | { type: "em"; children: MarkdownInline[] }
  | { type: "code"; text: string }
  | { type: "link"; href: string; children: MarkdownInline[] };

/** A column's alignment from the divider row: `:--` left, `--:` right, `:-:` center, `---` unset. */
export type MarkdownAlign = "left" | "right" | "center" | null;

export type MarkdownBlock =
  | { type: "heading"; level: 1 | 2 | 3; children: MarkdownInline[] }
  | { type: "paragraph"; children: MarkdownInline[] }
  | { type: "list"; ordered: boolean; items: MarkdownInline[][] }
  | { type: "table"; header: MarkdownInline[][]; align: MarkdownAlign[]; rows: MarkdownInline[][][] }
  | { type: "code"; text: string };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
const FENCE = /^\s*```/;
const SAFE_LINK = /^https:\/\/[^\s]+$/;

export function parseMarkdown(source: string): MarkdownBlock[] {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      i += 1;
      continue;
    }

    if (FENCE.test(line)) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i] ?? "")) code.push(lines[i++] ?? "");
      i += 1;
      blocks.push({ type: "code", text: code.join("\n") });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      const level = Math.min(heading[1]?.length ?? 1, 3) as 1 | 2 | 3;
      blocks.push({ type: "heading", level, children: parseInline(heading[2] ?? "") });
      i += 1;
      continue;
    }

    if (TABLE_ROW.test(line) && TABLE_DIVIDER.test(lines[i + 1] ?? "")) {
      const header = splitRow(line).map(parseInline);
      const align = splitRow(lines[i + 1] ?? "").map(toAlign);
      i += 2;
      const rows: MarkdownInline[][][] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i] ?? "")) rows.push(splitRow(lines[i++] ?? "").map(parseInline));
      blocks.push({ type: "table", header, align, rows });
      continue;
    }

    const listMatcher = BULLET.test(line) ? BULLET : NUMBERED.test(line) ? NUMBERED : null;
    if (listMatcher) {
      const items: MarkdownInline[][] = [];
      while (i < lines.length && listMatcher.test(lines[i] ?? "")) {
        items.push(parseInline(listMatcher.exec(lines[i] ?? "")?.[1] ?? ""));
        i += 1;
      }
      blocks.push({ type: "list", ordered: listMatcher === NUMBERED, items });
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length && isParagraphLine(lines[i] ?? "", lines[i + 1] ?? "")) paragraph.push((lines[i++] ?? "").trim());
    blocks.push({ type: "paragraph", children: parseInline(paragraph.join(" ")) });
  }

  return blocks;
}

function isParagraphLine(line: string, next: string): boolean {
  if (line.trim() === "" || FENCE.test(line) || HEADING.test(line) || BULLET.test(line) || NUMBERED.test(line)) return false;
  return !(TABLE_ROW.test(line) && TABLE_DIVIDER.test(next));
}

function toAlign(divider: string): MarkdownAlign {
  const starts = divider.startsWith(":");
  const ends = divider.endsWith(":");
  if (starts && ends) return "center";
  if (ends) return "right";
  return starts ? "left" : null;
}

function splitRow(row: string): string[] {
  return row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
}

/** Inline spans, earliest match first; unmatched markers stay as text. */
export function parseInline(text: string): MarkdownInline[] {
  const patterns: Array<{ regex: RegExp; build: (match: RegExpExecArray) => MarkdownInline }> = [
    { regex: /`([^`]+)`/, build: (m) => ({ type: "code", text: m[1] ?? "" }) },
    { regex: /\*\*(.+?)\*\*/, build: (m) => ({ type: "strong", children: parseInline(m[1] ?? "") }) },
    { regex: /\[([^\]]+)\]\(([^)\s]+)\)/, build: linkOrText },
    { regex: /(?<![\w*])\*(?!\s)(.+?)(?<!\s)\*(?![\w*])|(?<!\w)_(?!\s)(.+?)(?<!\s)_(?!\w)/, build: (m) => ({ type: "em", children: parseInline(m[1] ?? m[2] ?? "") }) },
  ];

  const result: MarkdownInline[] = [];
  let rest = text;
  while (rest.length > 0) {
    let earliest: { index: number; length: number; node: MarkdownInline } | null = null;
    for (const { regex, build } of patterns) {
      const match = regex.exec(rest);
      if (match && (earliest === null || match.index < earliest.index)) {
        earliest = { index: match.index, length: match[0].length, node: build(match) };
      }
    }
    if (!earliest) {
      result.push({ type: "text", text: rest });
      break;
    }
    if (earliest.index > 0) result.push({ type: "text", text: rest.slice(0, earliest.index) });
    result.push(earliest.node);
    rest = rest.slice(earliest.index + earliest.length);
  }
  return result;
}

/** Only absolute https links become links; anything else keeps its label as plain text. */
function linkOrText(match: RegExpExecArray): MarkdownInline {
  const label = match[1] ?? "";
  const href = match[2] ?? "";
  return SAFE_LINK.test(href) ? { type: "link", href, children: parseInline(label) } : { type: "text", text: label };
}
